/**
 * How long after the broker's last change a step keeps its draft: long enough not to save in the middle of a word,
 * short enough that a broker who stops has their work kept before they think to leave.
 */
export const AUTOSAVE_DELAY_MS = 1500;

/** Whether a save is waiting for the broker to stop, under way, or neither. */
export type AutosavePhase = 'idle' | 'pending' | 'saving';

/** How the last save ended: none yet, or one refused for a reason the broker was told elsewhere, as a conflict. */
export type AutosaveOutcome = 'none' | 'saved' | 'failed';

export interface AutosaveState {
  readonly phase: AutosavePhase;
  readonly outcome: AutosaveOutcome;
  /** A change came while a save was under way: one more save follows it, for every change made meanwhile. */
  readonly queued: boolean;
}

export const AUTOSAVE_START: AutosaveState = {
  phase: 'idle',
  outcome: 'none',
  queued: false,
};

/**
 * What happens to a step's autosave: the broker changed something the server doesn't have, or changed it back to what
 * it has; a save started; and it was kept, refused or failed.
 */
export type AutosaveEvent =
  | 'changed'
  | 'unchanged'
  | 'started'
  | 'kept'
  | 'refused'
  | 'failed';

const settled = (
  state: AutosaveState,
  outcome: AutosaveOutcome
): AutosaveState => ({
  phase: state.queued ? 'pending' : 'idle',
  outcome,
  queued: false,
});

export function autosaveAfter(
  state: AutosaveState,
  event: AutosaveEvent
): AutosaveState {
  switch (event) {
    case 'changed':
      return state.phase === 'saving'
        ? { ...state, queued: true }
        : { ...state, phase: 'pending' };
    case 'unchanged':
      return state.phase === 'saving' ? state : { ...state, phase: 'idle' };
    case 'started':
      return { ...state, phase: 'saving', queued: false };
    case 'kept':
      return settled(state, 'saved');
    case 'refused':
      return settled(state, 'none');
    case 'failed':
      return settled(state, 'failed');
  }
}

export const SAVING_LINE = 'Saving…';
export const SAVED_LINE = 'Saved';
export const SAVE_FAILED_LINE = "Couldn't save. We'll try again.";

/**
 * What the step's footer says of its autosave. Saved only while nothing has changed since: a change waiting to be
 * saved says nothing, rather than saved. A failure is said until the next save starts, which the next change brings.
 */
export function autosaveLine({ phase, outcome }: AutosaveState): string | null {
  if (phase === 'saving') {
    return SAVING_LINE;
  }
  if (outcome === 'failed') {
    return SAVE_FAILED_LINE;
  }
  return phase === 'idle' && outcome === 'saved' ? SAVED_LINE : null;
}

interface WhenDue {
  /** The step keeps drafts: it isn't committed. */
  readonly enabled: boolean;
  /** One of the autosave's own saves is under way. */
  readonly saving: boolean;
  /** The quote is being written otherwise: the step's commit, or a save the broker asked for. */
  readonly writing: boolean;
  /** The step holds a change the server doesn't have. */
  readonly dirty: boolean;
}

/**
 * What an autosave does once the broker has stopped: save; try again later, as another write is under way and the
 * version it carries would race this one; or nothing, as the step is committed or has nothing new.
 */
export function whenDue({
  enabled,
  saving,
  writing,
  dirty,
}: WhenDue): 'save' | 'later' | 'skip' {
  if (!enabled) {
    return 'skip';
  }
  if (saving || writing) {
    return 'later';
  }
  return dirty ? 'save' : 'skip';
}
